import React, { useEffect, Fragment, useState } from "react";
import { closeModal, openModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form } from "formik";
import SubmitButton from "../Forms/SubmitButton";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

export default function ModalParkingBay(props) {
  const dispatch = useDispatch();
  const { current_staff } = useSelector((state) => state.auth);
  const [open] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bayNumber, setBayNumber] = useState(null);
  const [editedBayNumber, setEditedBayNumber] = useState("");
  const [isMapped, setIsMapped] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (props.thisbed) {
      const bayNumber = props.thisbed.getAttribute("bay_number");
      const isMapped = props.thisbed.classList.contains("mapped");

      setBayNumber(bayNumber);
      setIsMapped(isMapped);

      if (isMapped) {
        // If we have a bay number but no parking_bay_id, fetch it
        if (bayNumber && !props.thisbed.getAttribute("parking_bay_id")) {
          fetchParkingBayId(bayNumber);
        }
        fetchParkingBayBookings();
      } else {
        setLoaded(true);
      }
    }
  }, [props.thisbed]);

  const fetchParkingBayId = async (bayNumber) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}parking-bays?floor_id=${props.floor_id}&bay_number=${bayNumber}`
      );

      if (response.data && response.data.length > 0) {
        const parkingBay = response.data[0];
        props.thisbed.setAttribute("parking_bay_id", parkingBay.id);

        // Save the updated SVG map
        let map_svg = document.querySelector(".map_svg").innerHTML;
        await axios.post(
          `${process.env.REACT_APP_API_URL}save_svg_map/${props.floor_id}`,
          { map: map_svg }
        );
      }
    } catch (error) {
      console.error("Error fetching parking bay ID:", error);
    }
  };

  const fetchParkingBayBookings = async () => {
    setLoading(true);
    try {
      const parkingBayId = props.thisbed.getAttribute("parking_bay_id");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}get_parking_bay_bookings/${parkingBayId}`
      );
      setBookings(response.data);
    } catch (error) {
      console.error("Error fetching parking bay bookings:", error);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    setEditedBayNumber(bayNumber || "");
  }, [bayNumber]);

  const handleBayNumberChange = (e) => {
    setEditedBayNumber(e.target.value);
    setHasChanges(true);
  };

  const handleSaveBayNumber = async () => {
    if (!editedBayNumber.trim() || !hasChanges) {
      console.log("No changes to save or empty bay number");
      return;
    }

    setIsSaving(true);
    try {
      const parkingBayId = props.thisbed.getAttribute("parking_bay_id");
      const newBayNumber = editedBayNumber.trim();
      
      // Update the bay number in the database
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}parking-bays/${parkingBayId}`,
        { bay_number: newBayNumber }
      );

      // Update the SVG element
      if (props.thisbed) {
        // Update the bay_number attribute
        props.thisbed.setAttribute("bay_number", newBayNumber);
        
        // Find and update the text element inside the SVG
        const textElement = props.thisbed.querySelector('text');
        if (textElement) {
          textElement.textContent = newBayNumber;
        }

        // Save the updated SVG to disk
        try {
          const mapSvg = document.querySelector('.map_svg');
          if (mapSvg) {
            const svgContent = mapSvg.innerHTML;
            await axios.post(
              `${process.env.REACT_APP_API_URL}save_svg_map/${props.floor_id}`,
              { map: svgContent }
            );
          }
        } catch (saveError) {
          console.error("Error saving SVG:", saveError);
          // Continue even if SVG save fails, as the bay number was updated successfully
        }
        
        // Trigger a custom event to notify other components about the update
        const event = new CustomEvent('bayNumberUpdated', {
          detail: { 
            element: props.thisbed,
            bayNumber: newBayNumber 
          }
        });
        document.dispatchEvent(event);
      }
      
      // Update component state
      setBayNumber(newBayNumber);
      setHasChanges(false);
      toast.success("Bay number updated successfully");
    } catch (error) {
      console.error("Error updating bay number:", {
        error,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers,
      });
      toast.error(`Failed to update bay number: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleMapParkingBay = async (values) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}parking-bays`,
        {
          floor_id: props.floor_id,
          bay_number: values.bay_number,
        }
      );

      // Update the SVG element
      props.thisbed.classList.add("mapped");
      props.thisbed.setAttribute("bay_number", values.bay_number);
      props.thisbed.setAttribute("parking_bay_id", response.data.id);

      // Remove the style attribute to allow the fill attribute to take effect
      props.thisbed.removeAttribute("style");

      // Get the SVG content and remove fill attributes
      let map_svg = document.querySelector(".map_svg").innerHTML;
      map_svg = removeFillAttributes(map_svg);

      // Save the updated SVG map
      await axios.post(
        `${process.env.REACT_APP_API_URL}save_svg_map/${props.floor_id}`,
        { map: map_svg }
      );

      toast.success("Parking bay mapped successfully");
      dispatch(closeModal());
    } catch (error) {
      console.error("Error mapping parking bay:", error);
      toast.error("Failed to map parking bay");
    }
  };

  const handleSaveAndReload = async (values) => {
    try {
      await handleMapParkingBay(values);
      window.location.reload();
    } catch (error) {
      console.error("Error in save and reload:", error);
    }
  };

  // Function to remove fill attributes
  function removeFillAttributes(svgString) {
    return svgString.replace(/fill="[^"]*"/g, "");
  }

  const delinkBed = async (bookingId) => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}delink_user_bed`, {
        booking_id: bookingId,
        user: current_staff,
        move_location: 1,
      });

      toast.success("Booking delinked successfully");
      fetchParkingBayBookings();
    } catch (error) {
      console.error("Error delinking booking:", error);
      toast.error("Failed to delink booking");
    }
  };

  const handleDeleteParkingBay = async () => {
    if (!window.confirm("Are you sure you want to delete this parking bay?")) {
      return;
    }

    setDeleting(true);
    try {
      console.log(props.thisbed);
      const parkingBayId = props.thisbed.getAttribute("parking_bay_id");

      if (!parkingBayId) {
        throw new Error("Parking bay ID not found");
      }

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}parking-bays/${parkingBayId}`
      );

      if (response.status === 204) {
        // Update the SVG element
        props.thisbed.classList.remove("mapped");
        props.thisbed.removeAttribute("bay_number");
        props.thisbed.removeAttribute("parking_bay_id");

        // Remove the style attribute to allow the fill attribute to take effect
        props.thisbed.removeAttribute("style");

        // Get the SVG content and remove fill attributes
        let map_svg = document.querySelector(".map_svg").innerHTML;
        map_svg = removeFillAttributes(map_svg);

        // Save the updated SVG map
        await axios.post(
          `${process.env.REACT_APP_API_URL}save_svg_map/${props.floor_id}`,
          { map: map_svg }
        );

        toast.success("Parking bay deleted successfully");
        dispatch(closeModal());
        window.location.reload();
      } else {
        throw new Error("Failed to delete parking bay");
      }
    } catch (error) {
      console.error("Error deleting parking bay:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.message === "Parking bay ID not found") {
        toast.error("Could not find parking bay ID. Please try again.");
      } else {
        toast.error("Failed to delete parking bay");
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-10 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  onClick={() => dispatch(closeModal())}
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900"
                  >
                    Parking Bay Details
                  </Dialog.Title>

                  {loaded ? (
                    isMapped ? (
                      loading ? (
                        <div className="text-center py-4">Loading...</div>
                      ) : (
                        <div className="mt-4">
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Bay Number
                            </label>
                            <div className="flex space-x-2">
                              <input
                                type="text"
                                value={editedBayNumber || ""}
                                onChange={handleBayNumberChange}
                                className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                disabled={!isMapped || isSaving}
                              />
                              <button
                                type="button"
                                onClick={handleSaveBayNumber}
                                disabled={
                                  !hasChanges ||
                                  isSaving ||
                                  !editedBayNumber.trim()
                                }
                                className={`px-4 py-2 rounded-md text-sm font-medium text-white ${
                                  !hasChanges ||
                                  isSaving ||
                                  !editedBayNumber.trim()
                                    ? "bg-gray-300 cursor-not-allowed"
                                    : "bg-indigo-600 hover:bg-indigo-700"
                                }`}
                              >
                                {isSaving ? "Saving..." : "Save"}
                              </button>
                            </div>
                          </div>
                          {bookings.length > 0 ? (
                            <div className="mt-4">
                              <h4 className="text-md font-medium mb-2">
                                Current Vehicles:
                              </h4>
                              {bookings.map((booking) => (
                                <div
                                  key={booking.id}
                                  className="mb-4 p-4 border rounded-lg"
                                >
                                  <p>
                                    <strong>Booking Reference:</strong>{" "}
                                    {console.log("Booking data:", booking)}
                                    <Link
                                      to={`/booking/management?user_id=${booking?.user?.id}&booking_id=${booking.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 underline"
                                    >
                                      {booking.booking_reference}
                                    </Link>
                                  </p>
                                  <p>
                                    <strong>Resident:</strong>{" "}
                                    {booking.user?.name} {booking.user?.surname}
                                  </p>
                                  <p>
                                    <strong>Unit:</strong>{" "}
                                    {booking.unit?.name || "N/A"}
                                  </p>
                                  <p>
                                    <strong>Vehicle:</strong>{" "}
                                    {booking.vehicle?.license_plate}
                                    {booking.vehicle?.make &&
                                      ` - ${booking.vehicle.make}`}
                                    {booking.vehicle?.model &&
                                      ` ${booking.vehicle.model}`}
                                    {booking.vehicle?.color &&
                                      ` (${booking.vehicle.color})`}
                                  </p>
                                  <p>
                                    <strong>Start Date:</strong>{" "}
                                    {booking.lease_start}
                                  </p>
                                  <p>
                                    <strong>End Date:</strong>{" "}
                                    {booking.lease_end}
                                  </p>

                                  {current_staff?.status === "Admin" && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="bg-dark_background hover:bg-dark_background focus:ring-dark_background text-white px-2 py-1 rounded-lg mr-2"
                                        onClick={() => delinkBed(booking.id)}
                                      >
                                        De-link
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="mt-4">
                              <p className="text-gray-500 mb-4">
                                No vehicles assigned to this parking bay.
                              </p>
                              <button
                                type="button"
                                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                                onClick={handleDeleteParkingBay}
                                disabled={deleting}
                              >
                                {deleting
                                  ? "Deleting..."
                                  : "Delete Parking Bay"}
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    ) : (
                      <Formik
                        initialValues={{ bay_number: bayNumber || "" }}
                        onSubmit={handleMapParkingBay}
                      >
                        {({ values, handleChange }) => (
                          <Form className="space-y-4">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Bay Number
                              </label>
                              <input
                                type="text"
                                style={{
                                  borderWidth: "1px",
                                }}
                                className="border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-md shadow-sm"
                                name="bay_number"
                                value={values.bay_number}
                                onChange={handleChange}
                              />
                            </div>
                            <div className="mt-5 sm:mt-6 flex space-x-4">
                              <button
                                type="submit"
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleSaveAndReload({
                                    bay_number: values.bay_number,
                                  })
                                }
                                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg"
                              >
                                Save & Reload
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    )
                  ) : (
                    <div>Loading...</div>
                  )}
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
